/**
 * Calculator Trigger Button.
 *
 * A small, inline button that opens the reseller calculator with
 * context-aware mode and pre-filled values from the surrounding data.
 * Appears as a subtle calculator icon that glows on hover.
 *
 * When an `onApply` callback is provided, the calculator will show an
 * "Apply" button next to results, allowing the user to send the
 * calculated value back to the triggering field with one click.
 */

import { Calculator } from "lucide-react";
import { type CalcMode, useCalculatorStore } from "@/stores/calculatorStore";

interface CalcTriggerProps {
  /** Which calculator mode to open. */
  mode: CalcMode;
  /** Pre-fill values (field names must match the mode's field names). */
  prefill: Record<string, string>;
  /** Label describing where this was triggered from. */
  source?: string;
  /** Optional label shown next to the icon. */
  label?: string;
  /** Size variant. */
  size?: "xs" | "sm";
  /** Callback to apply the calculator's primary result back to the field. */
  onApply?: (value: number) => void;
}

function CalcTrigger({
  mode,
  prefill,
  source,
  label,
  size = "xs",
  onApply,
}: CalcTriggerProps) {
  const openCalc = useCalculatorStore((s) => s.openCalc);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openCalc({ mode, prefill, source, onApply });
  };

  if (size === "sm") {
    return (
      <button
        onClick={handleClick}
        title={`Open calculator — ${mode}`}
        className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-2 py-1 text-[11px] font-medium text-[var(--text-tertiary)] transition-all hover:text-[var(--color-interactive-primary)] hover:border-[var(--color-interactive-primary)]/30 hover:bg-[var(--color-interactive-primary)]/5"
      >
        <Calculator className="h-3 w-3" />
        {label || "Calculate"}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      title={`Open calculator — ${mode}`}
      className="inline-flex items-center justify-center h-5 w-5 rounded text-[var(--text-tertiary)] transition-all hover:text-[var(--color-interactive-primary)] hover:bg-[var(--color-interactive-primary)]/10"
    >
      <Calculator className="h-3 w-3" />
    </button>
  );
}

export default CalcTrigger;
